/**
 * Programma di audit strutturato (AUD-F2.14b): modello versionato in Amministrazione (sezioni e campi) e valori
 * sull'audit, precompilati dai dati dell'audit. Il modello v1 riprende il Word di Pier «Piano_di_audit_fornitore.docx»
 * (MOD [__] Rev. 0, Rif. IST 01.1; PRO 01).
 */
import { z } from "zod";

/**
 * Nome del documento, in un punto solo lato server (PDF, mail, evidenza): Mario deve ancora decidere con Pier se chiamarlo
 * «Piano dell'audit» come la ISO 19011. Lato interfaccia il nome sta nella chiave i18n `pg_name` (g.it.json / g.en.json).
 */
export const PROGRAMME_NAME = { it: "Programma di audit", en: "Audit programme" } as const;

export const PROGRAMME_FIELD_TYPES = ["text", "textarea", "date", "choice", "multi", "table"] as const;
export type ProgrammeFieldType = (typeof PROGRAMME_FIELD_TYPES)[number];

/** Dati dell'audit da cui si precompila un campo (vedi programmePrefill nel backend). */
export const PROGRAMME_PREFILL = [
  "supplier.name",
  "supplier.code",
  "supplier.category",
  "supplier.contact",
  "factory.name",
  "factory.country",
  "factory.address",
  "factory.grade",
  "audit.code",
  "audit.notice",
  "audit.party",
  "audit.plannedDate",
  "audit.scope",
  "audit.client",
  "audit.checklist",
  "audit.team",
] as const;
export type ProgrammePrefill = (typeof PROGRAMME_PREFILL)[number];

const i18nText = z.object({ it: z.string().max(500), en: z.string().max(500).default("") });
export const ProgrammeOption = z.object({ value: z.string().min(1).max(60), label: i18nText });
export const ProgrammeColumn = z.object({ key: z.string().min(1).max(40), label: i18nText });
export const ProgrammeField = z.object({
  key: z.string().min(1).max(40).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
  label: i18nText,
  type: z.enum(PROGRAMME_FIELD_TYPES),
  help: i18nText.optional(),
  options: z.array(ProgrammeOption).max(30).optional(),
  columns: z.array(ProgrammeColumn).max(10).optional(),
  /** righe iniziali di una tabella (es. ruoli del gruppo di audit, voci della preparazione) */
  rows: z.array(z.record(z.string(), z.string().max(2000))).max(50).optional(),
  prefill: z.enum(PROGRAMME_PREFILL).optional(),
});
export const ProgrammeSection = z.object({
  key: z.string().min(1).max(40).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
  title: i18nText,
  fields: z.array(ProgrammeField).min(1).max(40),
});
export const ProgrammeSections = z
  .array(ProgrammeSection)
  .min(1)
  .max(20)
  .superRefine((secs, ctx) => {
    const sk = new Set<string>();
    for (const s of secs) {
      if (sk.has(s.key)) ctx.addIssue({ code: "custom", message: `Sezione duplicata: ${s.key}` });
      sk.add(s.key);
      const fk = new Set<string>();
      for (const f of s.fields) {
        if (fk.has(f.key)) ctx.addIssue({ code: "custom", message: `Campo duplicato in ${s.key}: ${f.key}` });
        fk.add(f.key);
        if ((f.type === "choice" || f.type === "multi") && !f.options?.length) ctx.addIssue({ code: "custom", message: `Il campo ${s.key}.${f.key} deve avere delle opzioni` });
        if (f.type === "table" && !f.columns?.length) ctx.addIssue({ code: "custom", message: `La tabella ${s.key}.${f.key} deve avere delle colonne` });
      }
    }
  });
export type ProgrammeFieldDef = z.infer<typeof ProgrammeField>;
export type ProgrammeSectionDef = z.infer<typeof ProgrammeSection>;

/** Valore di un campo: testo/data/scelta = stringa, multi = elenco di valori, tabella = righe. */
export type ProgrammeValue = string | string[] | Record<string, string>[];
/** Valori del programma: sezione → campo → valore. */
export type ProgrammeValues = Record<string, Record<string, ProgrammeValue>>;
export const ProgrammeValuesSchema = z.record(
  z.string(),
  z.record(z.string(), z.union([z.string().max(5000), z.array(z.string().max(200)).max(50), z.array(z.record(z.string(), z.string().max(2000))).max(100)])),
);

const t = (it: string, en: string) => ({ it, en });
const opt = (value: string, it: string, en: string) => ({ value, label: t(it, en) });
const col = (key: string, it: string, en: string) => ({ key, label: t(it, en) });

/** Modello v1: il Word di Pier, sezione per sezione. */
export const DEFAULT_PROGRAMME_TITLE = "Piano di audit fornitore – Rev. 0";
export const DEFAULT_PROGRAMME_SECTIONS: ProgrammeSectionDef[] = [
  {
    key: "supplier",
    title: t("Fornitore e stabilimento", "Supplier and factory"),
    fields: [
      { key: "vendor", label: t("Vendor (ragione sociale)", "Vendor (company name)"), type: "text", prefill: "supplier.name" },
      { key: "vendorCode", label: t("Vendor code", "Vendor code"), type: "text", prefill: "supplier.code" },
      { key: "factory", label: t("Factory / stabilimento", "Factory"), type: "text", prefill: "factory.name" },
      { key: "country", label: t("Paese", "Country"), type: "text", prefill: "factory.country" },
      { key: "address", label: t("Indirizzo del sito", "Site address"), type: "text", prefill: "factory.address" },
      { key: "contact", label: t("Referente fornitore", "Supplier contact"), type: "text", prefill: "supplier.contact" },
      { key: "category", label: t("Categoria merceologica", "Product category"), type: "text", prefill: "supplier.category" },
      { key: "tier3", label: t("Subfornitori Tier 3 coinvolti", "Tier 3 subcontractors involved"), type: "textarea" },
      { key: "grade", label: t("Grade (A/B/C)", "Grade (A/B/C)"), type: "text", prefill: "factory.grade" },
      { key: "evalClass", label: t("Classe Supplier Evaluation", "Supplier Evaluation class"), type: "text" },
      { key: "avgCompliance", label: t("AVG Compliance", "AVG Compliance"), type: "text" },
      { key: "buyerRisk", label: t("Buyer Critical Risk", "Buyer Critical Risk"), type: "text" },
    ],
  },
  {
    key: "mode",
    title: t("Tipologia e modalità", "Type and arrangements"),
    fields: [
      {
        key: "notice",
        label: t("Preavviso", "Notice"),
        type: "choice",
        prefill: "audit.notice",
        options: [opt("announced", "Annunciato", "Announced"), opt("semi", "Semi-annunciato", "Semi-announced"), opt("unannounced", "Non annunciato", "Unannounced")],
      },
      { key: "window", label: t("Finestra del semi-annunciato (15 gg: dal … al …)", "Semi-announced window (15 days: from … to …)"), type: "text" },
      {
        key: "party",
        label: t("Parte", "Party"),
        type: "choice",
        prefill: "audit.party",
        options: [opt("second", "Seconda parte (Brandart)", "Second party (Brandart)"), opt("third", "Terza parte (società / ente)", "Third party (company / body)")],
      },
      { key: "thirdParty", label: t("Società / ente (terza parte)", "Company / body (third party)"), type: "text" },
      {
        key: "mode",
        label: t("Modalità", "Mode"),
        type: "choice",
        options: [opt("onsite", "In presenza", "On site"), opt("remote", "Da remoto", "Remote"), opt("mixed", "Mista", "Mixed")],
      },
      { key: "date", label: t("Data", "Date"), type: "date", prefill: "audit.plannedDate" },
      { key: "manDays", label: t("Giorni/uomo", "Man-days"), type: "text" },
    ],
  },
  {
    key: "scope",
    title: t("Obiettivi, campo e criteri", "Objectives, scope and criteria"),
    fields: [
      { key: "objectives", label: t("Obiettivi", "Objectives"), type: "textarea" },
      { key: "scope", label: t("Campo (processi, reparti, turni, siti)", "Scope (processes, departments, shifts, sites)"), type: "textarea", prefill: "audit.scope" },
      {
        key: "criteria",
        label: t("Criteri", "Criteria"),
        type: "multi",
        options: [
          opt("iso9001", "ISO 9001 – Qualità", "ISO 9001 – Quality"),
          opt("iso14001", "ISO 14001 – Ambiente", "ISO 14001 – Environment"),
          opt("iso45001", "ISO 45001 – Salute e sicurezza", "ISO 45001 – Health and safety"),
          opt("sa8000", "SA8000 – Responsabilità sociale", "SA8000 – Social accountability"),
          opt("esg", "Criteri ESG applicabili", "Applicable ESG criteria"),
          opt("protocollo", "Protocollo d'intesa legalità filiere moda", "Fashion supply chain legality protocol"),
          opt("codice", "Codice di Condotta Fornitori Brandart", "Brandart Supplier Code of Conduct"),
          opt("cogente", "Normativa cogente del Paese", "Mandatory national regulations"),
          opt("cliente", "Requisiti specifici del cliente", "Client-specific requirements"),
          opt("checklist", "Check list utilizzata", "Checklist used"),
        ],
      },
      { key: "clientReq", label: t("Requisiti specifici del cliente", "Client-specific requirements"), type: "text", prefill: "audit.client" },
      { key: "checklistRev", label: t("Check list utilizzata (rev.)", "Checklist used (rev.)"), type: "text", prefill: "audit.checklist" },
    ],
  },
  {
    key: "team",
    title: t("Gruppo di audit", "Audit team"),
    fields: [
      {
        key: "members",
        label: t("Gruppo di audit", "Audit team"),
        type: "table",
        prefill: "audit.team",
        columns: [col("role", "Ruolo", "Role"), col("name", "Nome", "Name"), col("org", "Organizzazione", "Organisation"), col("lang", "Lingue", "Languages")],
        rows: [{ role: "Lead auditor" }, { role: "Auditor" }, { role: "Esperto tecnico" }, { role: "Interprete" }, { role: "Osservatore" }],
      },
    ],
  },
  {
    key: "prep",
    title: t("Preparazione", "Preparation"),
    fields: [
      {
        key: "items",
        label: t("Elementi esaminati prima dell'audit", "Items reviewed before the audit"),
        type: "table",
        columns: [col("item", "Elemento esaminato prima dell'audit", "Item reviewed before the audit"), col("done", "Fatto", "Done"), col("notes", "Note", "Notes")],
        rows: [
          { item: "Rapporti di audit precedenti" },
          { item: "NC aperte (Registro NC e Azioni Correttive – Fornitori)" },
          { item: "Punteggi e note del Vendor Rating" },
          { item: "Criticità dell'area geografica" },
          { item: "Certificazioni e documenti nel VMT (validità)" },
          { item: "Documenti richiesti al fornitore in anticipo" },
        ],
      },
    ],
  },
  {
    key: "agenda",
    title: t("Agenda", "Agenda"),
    fields: [
      {
        key: "rows",
        label: t("Agenda", "Agenda"),
        type: "table",
        columns: [
          col("date", "Data", "Date"),
          col("time", "Orario", "Time"),
          col("activity", "Attività / area", "Activity / area"),
          col("requirements", "Requisiti", "Requirements"),
          col("auditor", "Auditor", "Auditor"),
          col("supplier", "Interlocutori fornitore", "Supplier contacts"),
        ],
        // apertura e chiusura come da ISO 19011; il resto lo scrive l'auditor (agende tipo da Pier)
        rows: [{ activity: "Riunione di apertura" }, { activity: "Riunione di chiusura" }],
      },
    ],
  },
];

/** Oggetto e corpo della mail «programma inviato» (stessa forma per l'invio vero, l'anteprima e la prova). */
export function programmeMailText(p: { lang: "it" | "en"; code: string; supplier: string; factory: string; date: string; time: string; contact: string; version: number }): { subject: string; body: string } {
  const n = PROGRAMME_NAME[p.lang];
  if (p.lang === "en")
    return {
      subject: `[${p.code}] ${n} – ${p.supplier}`,
      body: `Please find attached the ${n.toLowerCase()} (version ${p.version}) for the audit at ${p.factory}.\n\nDate: ${p.date}${p.time ? `\nStart time: ${p.time}` : ""}\nBrandart contact: ${p.contact}\n\nPlease confirm receipt and let us know of any constraints on the agenda.`,
    };
  return {
    subject: `[${p.code}] ${n} – ${p.supplier}`,
    body: `In allegato il ${n.toLowerCase()} (versione ${p.version}) per l'audit presso ${p.factory}.\n\nData: ${p.date}${p.time ? `\nOrario di inizio: ${p.time}` : ""}\nReferente Brandart: ${p.contact}\n\nVi chiediamo di confermare la ricezione e di segnalarci eventuali vincoli sull'agenda.`,
  };
}

/* ---------------- API (AUD-F2.14b) ---------------- */
export const PROGRAMME_LANGS = ["it", "en"] as const;
export const ProgrammeModelBody = z.object({ title: z.string().trim().min(1).max(200), sections: ProgrammeSections, note: z.string().max(500).optional().default("") });
export const ProgrammeDocSaveBody = z.object({ values: ProgrammeValuesSchema });
export const ProgrammeIssueBody = z.object({ lang: z.enum(PROGRAMME_LANGS).default("it") });
export const ProgrammeSendBody = z.object({
  lang: z.enum(PROGRAMME_LANGS).default("it"),
  to: z.array(z.object({ name: z.string().max(200).default(""), email: z.string().trim().email() })).min(1).max(10),
});
export const ProgrammePreviewQuery = z.object({ lang: z.enum(PROGRAMME_LANGS).default("it") });

export interface ProgrammeModelDto {
  id: string;
  v: number;
  title: string;
  sections: ProgrammeSectionDef[];
  active: boolean;
  note: string;
  createdById: string | null;
  createdAt: string;
}
export interface ProgrammeRecipientDto {
  name: string;
  email: string;
  /** user = referente con utenza, supplier = contatto dell'anagrafica, contact = contatto VendorHub */
  source: string;
}
export interface AuditProgrammeDocDto {
  model: ProgrammeModelDto;
  /** valori correnti (salvati sopra la precompilazione) e precompilazione pura, per «ripristina dai dati dell'audit» */
  values: ProgrammeValues;
  prefill: ProgrammeValues;
  current: { v: number; savedAt: string; savedById: string | null } | null;
  versions: { v: number; savedAt: string; savedById: string | null; modelV: number }[];
  /** ultimo PDF emesso (versione dell'evidenza) */
  issued: { evidenceId: string; v: number; at: string; sha256: string | null; byId: string | null } | null;
  sent: { at: string; to: string; version: number | null; via: string | null } | null;
  recipients: ProgrammeRecipientDto[];
  /** MAIL_SEND_MODE del server: con «tests» le mail ai fornitori non partono (solo le prove a me) */
  mailMode: "all" | "tests" | "off";
  allowed: boolean;
}
export interface ProgrammeSendResultDto {
  doc: AuditProgrammeDocDto;
  recipients: string[];
  mailMode: "all" | "tests" | "off";
}
